import { useI18n } from '@/i18n/I18nProvider'
import ContactForm from '@/components/ContactForm'

/**
 * The closing block above the footer on every document — **the form itself**.
 *
 * It was a wide gradient card with a headline, one blue pill button back to
 * Contact Us and a cut-out photograph (built to a reference design on
 * 2026-08-26). On 2026-08-30 it was asked to become the form: "sayfa
 * sonlarındaki contact us tasarımımız ve bazı yerlerdeki siyah kutucuktaki
 * iletişime geç kısmına onlar yerine artık direkt form koyuyoruz". A reader who
 * has just been convinced by a page can now start the conversation on it,
 * instead of being sent one click away to start it.
 *
 * Being here rather than in each page's `sections` array is what makes "every
 * page" true: 117 of this site's 154 documents are imported WordPress pages with
 * no `sections` array at all, and `Layout` dresses both paths. The two documents
 * that do NOT get it — the home page, which carries its own form section, and
 * Contact Us, which is the form — opt out in `Layout`'s `NO_CONTACT_BAND`.
 *
 * **No JavaScript, no state, no hooks beyond the dictionary**, for the same
 * reason as before: those 117 pages ship no client bundle, so anything needing
 * hydration would work on a quarter of the site and silently do nothing on the
 * rest. `ContactForm` is built to that rule too — even its "Other" box opens on
 * a CSS `:has()`.
 *
 * `id="contact"` is deliberate and is the anchor the rest of the site scrolls
 * to: a Download or Book a call button anywhere on a page can be `href="#contact"`
 * and land here. `scroll-mt-24` is what keeps the heading clear of the sticky
 * 66px header when it does. The home page's own form section carries the same
 * id, and cannot collide with this one because the home page has no band.
 */
export default function ContactBand() {
  const { t } = useI18n()
  const band = t.contactBand

  // `relative z-10` on the section is what keeps the footer's arc behind this
  // block. The arc lives inside the footer at `-z-10` and rises past the
  // footer's top edge into this band; the footer is a later positioned
  // sibling, so without a z-index here the curve is painted across the fields
  // and, most visibly, across the thank-you panel that replaces them after a
  // send ("bu ribbon formun önüne çıkıyor", 2026-09-01). The gradient card
  // this band used to be carried the same class for the same reason, and it
  // left with the card on 2026-08-30.
  return (
    <section id="contact" className="site-container relative z-10 scroll-mt-24 pb-16 pt-16">
      <div className="mx-auto flex max-w-4xl flex-col gap-10">
        {/* Centred over the form, the way the reference capture sets it: a big
            question, one quiet line under it, then the fields. */}
        <div className="text-center">
          <h2 className="text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl">
            {band.heading}
          </h2>
          {band.sub && (
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">{band.sub}</p>
          )}
        </div>
        {/* `grid` pairs the fields two to a row, which is what the reference
            shows and what eight fields need — one column would be a page and a
            half of scrolling at the bottom of every document on the site.
            `idPrefix` keeps these ids distinct from any other form that lands on
            the same page. */}
        <ContactForm {...band.form} idPrefix="band-" layout="grid" />
      </div>
    </section>
  )
}
